import { useState } from "react";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PlusIcon, X } from "lucide-react";
import { StockPointType } from "@/gen/auth/common_pb";
import { toast } from "@/components/ui/use-toast";
import { pharmacyClient } from "@/connect";
import { useRouter } from "@tanstack/react-router";

const stockPointTypes = [
  { value: StockPointType.SELLING.toString(), label: "Selling" },
  { value: StockPointType.RECEIVING.toString(), label: "Receiving" },
  { value: StockPointType.CONSUMING.toString(), label: "Consuming" },
];

const AddStockPointSheet = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [stockPointName, setStockPointName] = useState("");
  const [type, setType] = useState("");
  const [managerName, setManagerName] = useState("");
  const [shiftStart, setShiftStart] = useState("");
  const [shiftEnd, setShiftEnd] = useState("");
  const router = useRouter();

  const resetForm = () => {
    setStockPointName("");
    setType("");
    setManagerName("");
    setShiftStart("");
    setShiftEnd("");
  };

  const handleClose = () => {
    setIsOpen(false);
    resetForm();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);

      let stockPointType: StockPointType | undefined;
      switch (type) {
        case StockPointType.SELLING.toString():
          stockPointType = StockPointType.SELLING;
          break;
        case StockPointType.RECEIVING.toString():
          stockPointType = StockPointType.RECEIVING;
          break;
        case StockPointType.CONSUMING.toString():
          stockPointType = StockPointType.CONSUMING;
          break;
        default:
          stockPointType = undefined;
      }
      if (!stockPointType) {
        toast({
          title: "Error",
          description: "Invalid stock point type selected.",
          variant: "destructive",
          duration: 3000,
        });
        setLoading(false);
        return;
      }

      await pharmacyClient.createNewStockPoint({
        name: stockPointName,
        stockPointType: stockPointType,
        managerName,
        shiftStartTime: shiftStart,
        shiftEndTime: shiftEnd,
      })

      router.invalidate();

      toast({
        title: "Success",
        description: "Stock point added successfully!",
        variant: "success",
        duration: 3000,
      });
      resetForm();
      setIsOpen(false);
      setLoading(false);

    } catch (error) {
      console.error("Error adding stock point:", error);
      toast({
        title: "Error",
        description: "Failed to add stock point. Please try again.",
        variant: "destructive",
        duration: 3000,
      })
      setLoading(false);
    }
  };

  return (
    <Sheet open={isOpen}>
      <SheetTrigger asChild>
        <Button size="xs" variant="outline" className="flex items-center gap-1" onClick={() => setIsOpen(true)}>
          <PlusIcon className="w-4 h-4 text-black" />
          Add more stock points
        </Button>
      </SheetTrigger>
      <SheetContent className="overflow-y-auto flex flex-col h-full">
        <SheetHeader>
          <SheetTitle>Add a new stock point</SheetTitle>
          <SheetDescription>
            The following details are required to add a new stock point
          </SheetDescription>
          <SheetClose
            className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none disabled:pointer-events-none data-[state=open]:bg-secondary"
            onClick={handleClose}
          >
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </SheetClose>
        </SheetHeader>
        <form id="add-stock-point-form" onSubmit={handleSubmit} className="mt-4 space-y-4 flex-1 flex flex-col">
          <div>
            <p className="text-sm font-semibold mb-2">Stock Point Name</p>
            <Input
              placeholder="Stock Point Name"
              value={stockPointName}
              onChange={e => setStockPointName(e.target.value)}
              required
            />
          </div>
          <div>
            <p className="text-sm font-semibold mb-2">Type</p>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger>
                <SelectValue placeholder="Select Type" />
              </SelectTrigger>
              <SelectContent>
                {stockPointTypes.map(option => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <p className="text-sm font-semibold mb-2">Manager Name</p>
            <Input
              placeholder="Manager Name"
              value={managerName}
              onChange={e => setManagerName(e.target.value)}
              required
            />
          </div>
          <div className="flex gap-4">
            <div className="flex-1">
              <p className="text-sm font-semibold mb-2">Shift Start Time</p>
              <Input
                type="time"
                placeholder="08:00"
                value={shiftStart}
                onChange={e => setShiftStart(e.target.value)}
                required
              />
            </div>
            <div className="flex-1">
              <p className="text-sm font-semibold mb-2">Shift End Time</p>
              <Input
                type="time"
                placeholder="18:00"
                value={shiftEnd}
                onChange={e => setShiftEnd(e.target.value)}
                required
              />
            </div>
          </div>
        </form>
        <div className="mt-auto pt-4">
          <Button type="submit" form="add-stock-point-form" className="w-full" disabled={loading}>
            {loading ? "Adding..." : "Add Stock Point"}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default AddStockPointSheet; 